/**
 * Bug/Feedback Context — cattura diagnostica completa dello stato dell'app
 */

import { useClientStore } from '../stores/clientStore';
import { useDealStore } from '../stores/dealStore';
import { useLeadPipelineStore } from '../stores/leadPipelineStore';
import { useProjectStore } from '../stores/projectStore';
import { useCalendarStore } from '../stores/calendarStore';
import { useTeamChatStore } from '../stores/teamChatStore';
import { useWhatsAppChatStore } from '../stores/whatsappChatStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useSettingsStore } from '../stores/settingsStore';
import { usePermissionsStore } from '../stores/permissionsStore';
import { useTicketStore } from '../stores/ticketStore';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useBookingStore } from '../stores/bookingStore';
import { useMarketingStore } from '../stores/marketingStore';
import { useWhatsAppAgentStore } from '../stores/whatsappAgentStore';
import { useAnalyticsStore } from '../stores/analyticsStore';

export interface FeedbackContext {
  url: string;
  route: string;
  queryParams: string;
  timestamp: string;
  appVersion: string;
  user: {
    id?: string;
    email?: string;
    role?: string;
    fullName?: string;
  } | null;
  browser: {
    userAgent: string;
    language: string;
    platform: string;
    viewport: { width: number; height: number };
    screenResolution: { width: number; height: number };
    online: boolean;
    devicePixelRatio: number;
    cookiesEnabled: boolean;
  };
  performance: {
    memoryMB?: number;
    memoryLimitMB?: number;
    domNodes: number;
    loadedSince: string;
    navigationEntries: number;
  };
  consoleErrors: string[];
  globalState: Record<string, unknown>;
  pageState: Record<string, unknown>;
  /** Stack dell'errore, solo per le segnalazioni aperte dall'avviso automatico */
  errorStack?: string;
}

// ── Store globali (sempre catturati) ──────────────────────────────

function getGlobalState(): Record<string, unknown> {
  const notifications = useNotificationStore.getState();
  const settings = useSettingsStore.getState();
  const permissions = usePermissionsStore.getState();

  return {
    notifications: {
      unreadCount: notifications.unreadCount ?? 0,
      whatsappUnreadCount: notifications.whatsappUnreadCount ?? 0,
      totalCount: notifications.notifications?.length ?? 0,
      realtimeConnected: notifications.realtimeConnected,
      loading: notifications.loading,
      error: notifications.error,
    },
    settings: {
      organizationId: settings.organizationId,
      initialized: settings.initialized,
      retryCount: settings.retryCount,
      loading: settings.loading,
      error: settings.error,
    },
    permissions: {
      userId: permissions.userId,
      userRole: permissions.userRole,
      permissionsCount: permissions.permissions?.length ?? 0,
      disabledCount: permissions.disabledPermissions?.length ?? 0,
      globallyDisabled: permissions.globallyDisabledResources,
      lastFetch: permissions.lastFetch,
      loading: permissions.loading,
      error: permissions.error,
    },
  };
}

// ── Store per pagina (catturati in base alla route) ──────────────

const ROUTE_STORE_MAP: Record<string, () => Record<string, unknown>> = {
  '/clients': () => {
    const s = useClientStore.getState();
    return {
      clients: {
        count: s.clients?.length ?? 0,
        leadsCount: s.leads?.length ?? 0,
        selectedClient: s.selectedClient ? { id: s.selectedClient.id, name: s.selectedClient.business_name } : null,
        selectedUserId: s.selectedUserId,
        selectedSalesRepId: s.selectedSalesRepId,
        activeFilters: s.currentLeadsFilters,
        pagination: s.leadsPagination ? { pageSize: s.leadsPagination.pageSize, initialized: s.leadsPagination.initialized } : null,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/leads': () => {
    const s = useLeadPipelineStore.getState();
    return {
      leads: {
        pipelinesCount: s.pipelines?.length ?? 0,
        selectedPipelineId: s.selectedPipelineId,
        stagesCount: s.stages instanceof Map ? s.stages.size : Object.keys(s.stages || {}).length,
        hasLegacyLeads: s.hasLegacyLeads,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/deals': () => {
    const s = useDealStore.getState();
    return {
      deals: {
        count: s.deals?.length ?? 0,
        selectedUserId: s.selectedUserId,
        loading: s.loading,
        statusUpdateLoading: s.statusUpdateLoading,
        error: s.error,
      },
    };
  },

  '/projects': () => {
    const s = useProjectStore.getState();
    return {
      projects: {
        count: s.projects?.length ?? 0,
        tasksCount: s.tasks?.length ?? 0,
        selectedProject: s.selectedProject ? { id: s.selectedProject.id, name: s.selectedProject.name } : null,
        showArchived: s.showArchived,
        bulkInProgress: !!s.bulkCreateProgress,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/calendar': () => {
    const s = useCalendarStore.getState();
    return {
      calendar: {
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/team-chat': () => {
    const s = useTeamChatStore.getState();
    return {
      teamChat: {
        chatsCount: s.chats?.length ?? 0,
        selectedChatId: s.selectedChatId,
        selectedChatName: s.selectedChat?.()?.name || null,
        messagesInCache: Object.keys(s.messages || {}).length,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/whatsapp': () => {
    const s = useWhatsAppChatStore.getState();
    const agent = useWhatsAppAgentStore.getState();
    return {
      whatsapp: {
        chatsCount: s.chats?.length ?? 0,
        selectedChatId: s.selectedChatId,
        messagesCount: s.messages?.length ?? 0,
        pendingMessagesCount: s.pendingMessages?.length ?? 0,
        loading: s.loading,
        error: s.error,
      },
      whatsappAgent: {
        assignmentsCount: agent.assignments?.length ?? 0,
        pendingRequests: agent.requests?.length ?? 0,
        runsCount: agent.runs?.length ?? 0,
        hasDraftText: !!agent.pendingDraftText,
        loading: agent.loading,
        error: agent.error,
      },
    };
  },

  '/tickets': () => {
    const s = useTicketStore.getState();
    return {
      tickets: {
        count: s.tickets?.length ?? 0,
        selectedTicket: s.selectedTicket ? { id: s.selectedTicket.id, title: s.selectedTicket.title, status: s.selectedTicket.status } : null,
        commentsCount: s.ticketComments?.length ?? 0,
        activeFilters: s.filters,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/invoices': () => {
    const s = useInvoiceStore.getState();
    return {
      invoices: {
        count: s.invoices?.length ?? 0,
        loading: s.loading,
        error: s.error,
      },
    };
  },

  '/booking': () => {
    const s = useBookingStore.getState();
    return {
      booking: {
        eventTypesCount: s.eventTypes?.length ?? 0,
        bookingsCount: s.bookings?.length ?? 0,
        availabilityCount: s.availability?.length ?? 0,
        slug: s.userBookingSlug,
        loading: {
          eventTypes: s.eventTypesLoading,
          bookings: s.bookingsLoading,
          settings: s.settingsLoading,
        },
        error: s.bookingsError,
      },
    };
  },

  '/marketing': () => {
    const s = useMarketingStore.getState();
    return {
      marketing: {
        campaignsCount: s.campaigns?.length ?? 0,
        datePreset: s.datePreset,
        platform: s.platform,
        dateRange: s.dateRange,
        loading: {
          overview: s.overviewLoading,
          campaigns: s.campaignsLoading,
          trend: s.trendLoading,
        },
        error: s.error,
      },
    };
  },

  '/dashboard': () => {
    const s = useAnalyticsStore.getState();
    return {
      analytics: {
        dashboardDealsCount: s.dashboardDeals?.length ?? 0,
        dashboardDealsTimestamp: s.dashboardDealsTimestamp,
        loading: s.loading,
        dashboardDealsLoading: s.dashboardDealsLoading,
        error: s.error,
        dashboardDealsError: s.dashboardDealsError,
      },
    };
  },
};

function getPageState(pathname: string): Record<string, unknown> {
  // Match più lungo tra le route registrate
  const matchingKey = Object.keys(ROUTE_STORE_MAP)
    .filter(key => pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];

  if (matchingKey) {
    try {
      return ROUTE_STORE_MAP[matchingKey]();
    } catch {
      return { _captureError: 'Failed to capture page state' };
    }
  }
  return {};
}

// ── Funzione principale ──────────────────────────────────────────

export function captureFeedbackContext(user: { id?: string; email?: string; user_metadata?: Record<string, any> } | null): FeedbackContext {
  const perf = performance as any;
  const memoryMB = perf.memory ? Math.round(perf.memory.usedJSHeapSize / 1024 / 1024) : undefined;
  const memoryLimitMB = perf.memory ? Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024) : undefined;

  const loadedMinutes = performance.timeOrigin
    ? Math.round((Date.now() - performance.timeOrigin) / 1000 / 60)
    : 0;

  return {
    url: window.location.href,
    route: window.location.pathname,
    queryParams: window.location.search,
    timestamp: new Date().toISOString(),
    appVersion: '3.2.0',
    user: user
      ? {
          id: user.id,
          email: user.email,
          role: user.user_metadata?.role || 'N/A',
          fullName: user.user_metadata?.full_name || undefined,
        }
      : null,
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      screenResolution: { width: screen.width, height: screen.height },
      online: navigator.onLine,
      devicePixelRatio: window.devicePixelRatio,
      cookiesEnabled: navigator.cookieEnabled,
    },
    performance: {
      memoryMB,
      memoryLimitMB,
      domNodes: document.querySelectorAll('*').length,
      loadedSince: `${loadedMinutes} min`,
      navigationEntries: performance.getEntriesByType?.('navigation')?.length ?? 0,
    },
    consoleErrors: [...((window as any).__bugReportErrors || [])],
    globalState: getGlobalState(),
    pageState: getPageState(window.location.pathname),
  };
}
